import { useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export const LeadForm = ({ type = "contact", testid = "lead", dark = true }) => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", website: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const tsRef = useRef(Date.now());

    const set = (k) => (e) => {
        setDone(false);
        setForm({ ...form, [k]: e.target.value });
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/leads", { ...form, type, ts: tsRef.current });
            toast.success(data.message || "Submitted successfully!");
            setDone(true);
            setForm({ name: "", email: "", phone: "", company: "", message: "", website: "" });
            tsRef.current = Date.now();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-500/60 focus:bg-white/[0.07]";

    return (
        <form onSubmit={submit} className="space-y-4" data-testid={`${testid}-form`}>
            <div className="grid gap-4 sm:grid-cols-2">
                <input required minLength={2} data-testid={`${testid}-name-input`} className={inputCls} placeholder="Your Name *" value={form.name} onChange={set("name")} />
                <input required type="email" data-testid={`${testid}-email-input`} className={inputCls} placeholder="Email Address *" value={form.email} onChange={set("email")} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid={`${testid}-phone-input`} className={inputCls} placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} />
                <input data-testid={`${testid}-company-input`} className={inputCls} placeholder="Company Name" value={form.company} onChange={set("company")} />
            </div>
            <textarea
                required
                rows={5}
                data-testid={`${testid}-message-input`}
                className={`${inputCls} resize-none`}
                placeholder={type === "investor" ? "Tell us about your investment interest *" : "How can our AI employees help your business? *"}
                value={form.message}
                onChange={set("message")}
            />
            {/* honeypot — invisible to humans, bots fill it */}
            <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hp-field"
                value={form.website}
                onChange={set("website")}
                data-testid={`${testid}-honeypot-field`}
            />
            <label className="flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-slate-400" data-testid={`${testid}-consent-label`}>
                <input
                    type="checkbox"
                    required
                    data-testid={`${testid}-consent-checkbox`}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-white/20 bg-white/5 accent-cyan-500"
                />
                <span>
                    I agree to be contacted by WorkAssign AI via Call, WhatsApp or Email about my enquiry, and I accept the{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">Privacy Policy</a>.
                </span>
            </label>
            <button
                type="submit"
                disabled={loading}
                data-testid={`${testid}-submit-button`}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition-transform hover:scale-[1.01] disabled:opacity-60"
            >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending…" : type === "investor" ? "Submit Investor Enquiry" : "Send Message"}
            </button>
            {done && (
                <p className="text-center text-sm text-emerald-400" data-testid={`${testid}-success-message`}>
                    Received! Our team will reach out within 24 hours.
                </p>
            )}
            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-cyan-500" />
                Protected by anti-bot verification. Your data is never shared without consent.
            </p>
        </form>
    );
};
